/*
 * Invisible characters, found so that they can be shown (S12 §10.3). A bidi control can make a line
 * read differently from what it is — `RIGHT-TO-LEFT OVERRIDE` turns "txt.exe" into something that
 * looks like "exe.txt" — and a zero-width or tag character can hide words a reader never sees but a
 * tool, a search or a model does. Text a reader must be able to trust (an AI chat's prose, a command
 * Claude ran, a title Claude set) therefore draws each of them as a chip naming its code point.
 *
 * Three families are found: the bidi controls (U+061C, U+200E–200F, U+202A–202E, U+2066–2069), the
 * zero-width characters (U+200B–200D, U+2060, U+FEFF) and the tag characters (U+E0000–E007F). Two
 * uses of them are what they claim to be and stay intact, because an emoji broken into chips is a
 * defect rather than a warning: a zero-width joiner between two emoji (a woman technologist, the
 * rainbow flag), and the tag sequence of a subdivision flag (the black flag, the region's tag letters,
 * CANCEL TAG — Scotland's flag).
 *
 * The functions here are pure; `InvisibleText.tsx` draws their answer. The name each chip carries is
 * the character's Unicode name, which is data — the standard's identifier, the same in every
 * language — and not interface copy.
 */

/** One invisible character: the character itself, its code point as "U+202E", and its name. */
export interface InvisibleChar {
	char: string
	code: string
	name: string
}

/** A text cut at its invisible characters: runs of ordinary text, and the characters between them. */
export type TextPart = string | InvisibleChar

/** Every character the families above hold, as one class. */
const INVISIBLE =
	/[\u{061C}\u{200B}-\u{200F}\u{202A}-\u{202E}\u{2060}\u{2066}-\u{2069}\u{FEFF}\u{E0000}-\u{E007F}]/u

/**
 * A character a zero-width joiner may follow inside an emoji: the emoji, its presentation selector,
 * a skin tone.
 */
const EMOJI_BEFORE_JOINER = /^(?:\p{Extended_Pictographic}|\u{FE0F}|[\u{1F3FB}-\u{1F3FF}])$/u
const EMOJI = /\p{Extended_Pictographic}/u

/** A subdivision flag: the black flag, the region's tag letters, and CANCEL TAG. */
const FLAG_TAG_SEQUENCE = /^\u{1F3F4}[\u{E0020}-\u{E007E}]+\u{E007F}/u

const ZWJ = "\u{200D}"

const NAMES: Readonly<Record<number, string>> = {
	0x061c: "ARABIC LETTER MARK",
	0x200b: "ZERO WIDTH SPACE",
	0x200c: "ZERO WIDTH NON-JOINER",
	0x200d: "ZERO WIDTH JOINER",
	0x200e: "LEFT-TO-RIGHT MARK",
	0x200f: "RIGHT-TO-LEFT MARK",
	0x202a: "LEFT-TO-RIGHT EMBEDDING",
	0x202b: "RIGHT-TO-LEFT EMBEDDING",
	0x202c: "POP DIRECTIONAL FORMATTING",
	0x202d: "LEFT-TO-RIGHT OVERRIDE",
	0x202e: "RIGHT-TO-LEFT OVERRIDE",
	0x2060: "WORD JOINER",
	0x2066: "LEFT-TO-RIGHT ISOLATE",
	0x2067: "RIGHT-TO-LEFT ISOLATE",
	0x2068: "FIRST STRONG ISOLATE",
	0x2069: "POP DIRECTIONAL ISOLATE",
	0xfeff: "ZERO WIDTH NO-BREAK SPACE",
	0xe0001: "LANGUAGE TAG",
	0xe007f: "CANCEL TAG",
}

/**
 * The names of the printable ASCII characters from U+0020 to U+002F, U+003A to U+0040, U+005B to
 * U+0060 and U+007B to U+007E, which a tag character mirrors ("TAG COMMERCIAL AT"). Letters and
 * digits are named by rule below.
 */
const ASCII_NAMES: Readonly<Record<string, string>> = {
	" ": "SPACE",
	"!": "EXCLAMATION MARK",
	'"': "QUOTATION MARK",
	"#": "NUMBER SIGN",
	$: "DOLLAR SIGN",
	"%": "PERCENT SIGN",
	"&": "AMPERSAND",
	"'": "APOSTROPHE",
	"(": "LEFT PARENTHESIS",
	")": "RIGHT PARENTHESIS",
	"*": "ASTERISK",
	"+": "PLUS SIGN",
	",": "COMMA",
	"-": "HYPHEN-MINUS",
	".": "FULL STOP",
	"/": "SOLIDUS",
	":": "COLON",
	";": "SEMICOLON",
	"<": "LESS-THAN SIGN",
	"=": "EQUALS SIGN",
	">": "GREATER-THAN SIGN",
	"?": "QUESTION MARK",
	"@": "COMMERCIAL AT",
	"[": "LEFT SQUARE BRACKET",
	"\\": "REVERSE SOLIDUS",
	"]": "RIGHT SQUARE BRACKET",
	"^": "CIRCUMFLEX ACCENT",
	_: "LOW LINE",
	"`": "GRAVE ACCENT",
	"{": "LEFT CURLY BRACKET",
	"|": "VERTICAL LINE",
	"}": "RIGHT CURLY BRACKET",
	"~": "TILDE",
}

const DIGIT_NAMES = ["ZERO", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE"]

/** "U+202E": the code point, four hex digits at least, as the standard writes it. */
function codeOf(cp: number): string {
	return `U+${cp.toString(16).toUpperCase().padStart(4, "0")}`
}

/** The Unicode name of one invisible character; an unassigned tag code point is named by its code. */
function nameOf(cp: number): string {
	const known = NAMES[cp]
	if (known !== undefined) return known
	if (cp >= 0xe0020 && cp <= 0xe007e) {
		const mirrored = String.fromCodePoint(cp - 0xe0000)
		if (mirrored >= "a" && mirrored <= "z")
			return `TAG LATIN SMALL LETTER ${mirrored.toUpperCase()}`
		if (mirrored >= "A" && mirrored <= "Z") return `TAG LATIN CAPITAL LETTER ${mirrored}`
		if (mirrored >= "0" && mirrored <= "9")
			return `TAG DIGIT ${DIGIT_NAMES[Number(mirrored)] ?? ""}`
		return `TAG ${ASCII_NAMES[mirrored] ?? codeOf(cp)}`
	}
	return codeOf(cp)
}

/** Whether a text holds any character this file would draw: the cheap question most texts answer no to. */
export function hasInvisible(text: string): boolean {
	return INVISIBLE.test(text)
}

/**
 * The text cut at its invisible characters. Joining the string parts and the `char` of every other
 * part gives the text back exactly, which is what lets a copy keep the original.
 */
export function splitInvisible(text: string): TextPart[] {
	if (!hasInvisible(text)) return text === "" ? [] : [text]
	const parts: TextPart[] = []
	const chars = Array.from(text)
	let run = ""
	for (let i = 0; i < chars.length; i++) {
		const ch = chars[i] ?? ""
		if (ch === "\u{1F3F4}") {
			const flag = FLAG_TAG_SEQUENCE.exec(chars.slice(i).join(""))
			if (flag !== null) {
				run += flag[0]
				i += Array.from(flag[0]).length - 1
				continue
			}
		}
		const joinsEmoji =
			ch === ZWJ && EMOJI_BEFORE_JOINER.test(chars[i - 1] ?? "") && EMOJI.test(chars[i + 1] ?? "")
		if (!INVISIBLE.test(ch) || joinsEmoji) {
			run += ch
			continue
		}
		if (run !== "") parts.push(run)
		run = ""
		const cp = ch.codePointAt(0) ?? 0
		parts.push({ char: ch, code: codeOf(cp), name: nameOf(cp) })
	}
	if (run !== "") parts.push(run)
	return parts
}

/**
 * The text without its invisible characters, where a chip cannot be drawn — the page title, an
 * attribute. The emoji the chips would have left intact stay intact here too.
 */
export function stripInvisible(text: string): string {
	if (!hasInvisible(text)) return text
	return splitInvisible(text)
		.filter((part): part is string => typeof part === "string")
		.join("")
}
